// Third Party
import * as React from 'react';
import Image from 'next/image';
import cx from 'classnames';

// Utils
import { routes } from '@routes';
import { imagekitLoader } from '@utils/imagekit-loader';
import { GRAY_PIXEL_DATA_URL } from '@utils/constants';
import { chunkArray } from '@utils/array-helpers';
import { Props, hiddenStyles } from '.';

// UI
import { Icon, IconNames } from '@components/icon';
import { Typography } from '@components/typography';
import { concatenateAndReplace } from '@utils/test-helpers';

export const ThreeUpDesktopSection: React.FC<Props> = ({ listings }) => {
  const lastIndex = Math.ceil(listings.length / 3) - 1;

  const [currentListingChunk, setCurrentListingChunk] = React.useState<{
    translateX: number;
    index: number;
  }>({
    translateX: 0,
    index: 0,
  });

  const prevSlide = () => {
    if (currentListingChunk.index > 0) {
      setCurrentListingChunk((val) => {
        return {
          translateX: val.translateX + 100,
          index: val.index - 1,
        };
      });
    }
  };

  const nextSlide = () => {
    if (currentListingChunk.index < lastIndex) {
      setCurrentListingChunk((val) => {
        return {
          translateX: val.translateX - 100,
          index: val.index + 1,
        };
      });
    }
  };

  return (
    <div className="hidden md:block 2xl:hidden">
      <div className="flex flex-row justify-between items-center mb-2 py-4">
        <Typography component="h5" variant="title6" variantM="title4">
          Other Properties from this Host
        </Typography>
        <div className="space-x-2 mb-2">
          <button
            disabled={currentListingChunk.index === 0}
            onClick={prevSlide}
            style={{ outline: 'none' }}
            className={cx(
              'focus:outline-none inline-flex items-center p-2 w-10 h-10 bg-white border border-grayscale-xlight hover:ring hover:ring-grayscale-xlight border-transparent rounded-full shadow-sm',
              {
                'hover:ring-0 cursor-not-allowed':
                  currentListingChunk.index === 0,
              }
            )}
            type="button"
          >
            <Icon
              name={IconNames.CHEVRON_LEFT}
              className="mx-auto my-auto w-4 h-4 text-gray-light"
            />
          </button>
          <button
            disabled={currentListingChunk.index === lastIndex}
            onClick={nextSlide}
            style={{ outline: 'none' }}
            className={cx(
              'focus:outline-none inline-flex items-center p-2 w-10 h-10 bg-white border border-grayscale-xlight hover:ring hover:ring-grayscale-xlight border-transparent rounded-full shadow-sm',
              {
                'hover:ring-0 cursor-not-allowed':
                  currentListingChunk.index === lastIndex,
              }
            )}
            type="button"
          >
            <Icon
              name={IconNames.CHEVRON_RIGHT}
              className="mx-auto my-auto w-4 h-4 text-gray-light"
            />
          </button>
        </div>
      </div>

      <div className="overflow-x-hidden overflow-y-hidden">
        <div
          className="overflow-x-visible overflow-y-visible whitespace-nowrap"
          style={{
            transform: `translateX(${currentListingChunk.translateX}%)`,
            transitionDelay: '0s, 0s, 0s',
            transitionDuration: '0.5s, 0.5s, 0.5s',
            transitionTimingFunction: ' ease, ease, ease',
          }}
        >
          {chunkArray(listings, 3)
            .map((listings, index) =>
              listings.map((listing) => {
                return (
                  <a
                    key={listing.name}
                    href={routes.listings({
                      slug: listing.slug,
                    })}
                    className="inline-block w-1/3 align-top px-2 whitespace-normal"
                    style={
                      currentListingChunk.index !== index ? hiddenStyles : {}
                    }
                  >
                    <div className="relative overflow-hidden rounded-md shadow-md aspect-[3/2] w-full">
                      <Image
                        layout="fill"
                        src={
                          listing.galleryAssets.length > 0
                            ? listing.galleryAssets[0].url || ''
                            : ''
                        }
                        alt={listing.name || 'listing result'}
                        placeholder="blur"
                        className="object-center object-cover shadow-md hover:ease-in hover:duration-200 hover:scale-105"
                        loader={imagekitLoader}
                        blurDataURL={GRAY_PIXEL_DATA_URL}
                        sizes="33vw"
                        lazyBoundary="400px"
                      />
                    </div>

                    <div>
                      <h4
                        className="text-xs font-semibold leading-tight mt-2 line-clamp-1"
                        data-testid={concatenateAndReplace(`${listing.name}`)}
                      >
                        {listing.name}
                      </h4>
                      <p className="text-xs leading-tight mt-1 line-clamp-2">
                        {listing.city + ', ' + listing.stateOrProvinceLongName}
                      </p>
                    </div>
                  </a>
                );
              })
            )
            .flat()}
        </div>
      </div>
    </div>
  );
};
